/**
 * Módulo Solicitudes de catálogo (spec SPEC_Modulo_SolicitudesDeCatalogo) · el
 * solicitante pide dar de alta un elemento que falta en un catálogo; los
 * RESPONSABLES de cada tipo (por rol, configurable) lo atienden desde la bandeja
 * `/solicitudes-catalogo`; el administrador configura los tipos.
 */
import { axiosClient } from "@/shared/api/axios-client";
import type { Paginado } from "@/lib/types";

export type EstadoSolicitudCatalogo = "Pendiente" | "Atendida" | "Rechazada" | "Cancelada";

/** Tipos de catálogo sobre los que se puede pedir un alta. */
export type TipoCatalogoSolicitud =
  | "Proveedor"
  | "CuentaContablePorClave"
  | "ClaveSat"
  | "CentroDeCosto"
  | "CuentaContable"
  | "EquivalenciaUnidad";

export interface SolicitudDeCatalogo {
  id: string;
  tipo: TipoCatalogoSolicitud;
  clave: string;
  /** Solo CeCo/cuenta contable · la organización para la que se pide. */
  numeroSociedad: string | null;
  descripcion: string | null;
  comentario: string | null;
  estado: EstadoSolicitudCatalogo;
  solicitanteId: string;
  solicitanteNombre: string | null;
  solicitudPagoId: string | null;
  numeroSolicitudPago: number | null;
  creadoAt: string;
  resueltaAt: string | null;
  respuestaComentario: string | null;
  /** Se atendió sola al darse de alta el elemento en su catálogo. */
  atendidaAutomaticamente: boolean;
  /** null = resolución aún no vista por el solicitante. */
  vistaPorSolicitanteAt: string | null;
}

/** Configuración de un tipo (la edita el administrador · los textos los ve el solicitante). */
export interface TipoDeSolicitudCatalogo {
  tipo: TipoCatalogoSolicitud;
  nombre: string;
  activo: boolean;
  rolesResponsables: string[];
  atencionAutomatica: boolean;
  rechazoExigeMotivo: boolean;
  textoBoton: string;
  /** «{clave}» se reemplaza por la clave pedida. */
  textoModal: string;
}

export interface CrearSolicitudCatalogoInput {
  tipo: TipoCatalogoSolicitud;
  clave: string;
  numeroSociedad?: string;
  descripcion?: string;
  comentario?: string;
  solicitudPagoId?: string;
  numeroSolicitudPago?: number;
}

export interface CrearSolicitudCatalogoResultado {
  solicitud: SolicitudDeCatalogo;
  /** true → ya había una pendiente para (tipo, clave) · no se duplicó. */
  yaExistia: boolean;
}

// --- Lado solicitante --------------------------------------------------------------

/** Tipos ACTIVOS con sus textos (botón y modal). */
export async function listarTiposActivosSolicitudCatalogo(): Promise<TipoDeSolicitudCatalogo[]> {
  const { data } = await axiosClient.get<TipoDeSolicitudCatalogo[]>("/api/catalogos/solicitudes/tipos");
  return data;
}

export async function crearSolicitudCatalogo(
  input: CrearSolicitudCatalogoInput
): Promise<CrearSolicitudCatalogoResultado> {
  const { data } = await axiosClient.post<CrearSolicitudCatalogoResultado>(
    "/api/catalogos/solicitudes",
    input
  );
  return data;
}

/** null cuando nadie ha pedido esa clave (el backend responde 204). */
export async function obtenerEstadoSolicitudCatalogo(
  tipo: string,
  clave: string,
  numeroSociedad?: string
): Promise<SolicitudDeCatalogo | null> {
  const response = await axiosClient.get<SolicitudDeCatalogo | "">("/api/catalogos/solicitudes/estado", {
    params: { tipo, clave, ...(numeroSociedad ? { numeroSociedad } : {}) },
  });
  return response.status === 204 || !response.data ? null : response.data;
}

/** El solicitante cancela SU solicitud pendiente (queda auditado). */
export async function cancelarMiSolicitudCatalogo(id: string, motivo?: string): Promise<SolicitudDeCatalogo> {
  const { data } = await axiosClient.post<SolicitudDeCatalogo>(
    `/api/catalogos/solicitudes/mias/${id}/cancelar`,
    { motivo: motivo || null }
  );
  return data;
}

export async function listarMisSolicitudesCatalogo(): Promise<SolicitudDeCatalogo[]> {
  const { data } = await axiosClient.get<SolicitudDeCatalogo[]>("/api/catalogos/solicitudes/mias");
  return data;
}

export async function contarMisResolucionesNoVistas(): Promise<number> {
  const { data } = await axiosClient.get<{ total: number }>("/api/catalogos/solicitudes/mias/no-vistas/count");
  return data.total;
}

export async function marcarMisSolicitudesVistas(): Promise<void> {
  await axiosClient.post("/api/catalogos/solicitudes/mias/marcar-vistas");
}

/** Quita la solicitud de MIS avisos (solo resueltas/canceladas · el registro persiste). */
export async function eliminarMiNotificacion(id: string): Promise<void> {
  await axiosClient.delete(`/api/catalogos/solicitudes/mias/${id}`);
}

// --- Bandeja de los responsables ---------------------------------------------------

export interface ListarSolicitudesCatalogoParams {
  estado?: string;
  tipo?: string;
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Bandeja PAGINADA server-side · solo los tipos que el usuario atiende. */
export async function listarSolicitudesCatalogo(
  params: ListarSolicitudesCatalogoParams = {}
): Promise<Paginado<SolicitudDeCatalogo>> {
  const { data } = await axiosClient.get<Paginado<SolicitudDeCatalogo>>("/api/solicitudes-catalogo", { params });
  return data;
}

export async function contarSolicitudesCatalogoPendientes(): Promise<number> {
  const { data } = await axiosClient.get<{ total: number }>("/api/solicitudes-catalogo/pendientes/count");
  return data.total;
}

/** Tipos que el usuario atiende (con su configuración) · vacío = no tiene bandeja. */
export async function misTiposResponsableCatalogo(): Promise<TipoDeSolicitudCatalogo[]> {
  const { data } = await axiosClient.get<TipoDeSolicitudCatalogo[]>("/api/solicitudes-catalogo/mis-tipos");
  return data;
}

export async function atenderSolicitudCatalogo(id: string, comentario?: string): Promise<SolicitudDeCatalogo> {
  const { data } = await axiosClient.post<SolicitudDeCatalogo>(`/api/solicitudes-catalogo/${id}/atender`, {
    comentario: comentario || null,
  });
  return data;
}

export async function rechazarSolicitudCatalogo(id: string, comentario?: string): Promise<SolicitudDeCatalogo> {
  const { data } = await axiosClient.post<SolicitudDeCatalogo>(`/api/solicitudes-catalogo/${id}/rechazar`, {
    comentario: comentario || null,
  });
  return data;
}

/** Quita la solicitud de la bandeja (solo resueltas/canceladas · soft-hide). */
export async function eliminarSolicitudCatalogoBandeja(id: string): Promise<void> {
  await axiosClient.delete(`/api/solicitudes-catalogo/${id}`);
}

// --- Configuración de los tipos (administrador) ------------------------------------

export async function listarTiposSolicitudCatalogoAdmin(): Promise<TipoDeSolicitudCatalogo[]> {
  const { data } = await axiosClient.get<TipoDeSolicitudCatalogo[]>("/api/admin/tipos-solicitud-catalogo");
  return data;
}

export async function actualizarTipoSolicitudCatalogo(
  t: TipoDeSolicitudCatalogo
): Promise<TipoDeSolicitudCatalogo> {
  const { tipo, ...body } = t;
  const { data } = await axiosClient.put<TipoDeSolicitudCatalogo>(`/api/admin/tipos-solicitud-catalogo/${tipo}`, body);
  return data;
}
